// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { ICondition } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import { makeSortHeader } from "guiutils";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";

import Condition from "view/comp/Condition";

// sort key of table
const TABLE_NAME = "t_conditions";

interface IConditionListProps {
  conditions: ICondition[];
  values: number[];
  tableSort: ITableSort | undefined;
  showId: boolean;
  showDetail: boolean;
  filterText?: string;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  // show condition graph if this site has new json condition block parameters
  const showDetail =
    state.para.parameter?.condition.every((parameter) =>
      parameter.blocks.every((block) => !isSet(block.itsSettings)),
    ) ?? false;

  const props: IConditionListProps = {
    conditions: state.para.parameter?.condition ?? [],
    values: state.data.condition,
    tableSort: state.gui.tableSort[TABLE_NAME],
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
    filterText: state.gui.searchFilterText,
    showDetail,
  };
  return props;
};

interface IConditionListDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IConditionListDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

class ConditionListComponent extends React.Component<
  IConditionListProps & IConditionListDispatch & RoutedProps
> {
  private getConditionList() {
    const filterTextLowerCase = this.props.filterText?.toLocaleLowerCase();
    const sortCol = (this.props.tableSort?.column ?? "name") as keyof ICondition | "value";

    // get list of used conditions
    const allList = Array.from(Array(this.props.conditions.length).keys());
    const filteredList = allList.filter((curIndex) => {
      const item = this.props.conditions[curIndex];
      return (
        item.webActive &&
        (!isSet(filterTextLowerCase) || item.name.toLocaleLowerCase().includes(filterTextLowerCase))
      );
    });
    const sortedList = filteredList.sort((a, b) => {
      const keyA =
        sortCol === "value" ? this.props.values[a] : this.props.conditions[a]?.[sortCol!];
      const keyB =
        sortCol === "value" ? this.props.values[b] : this.props.conditions[b]?.[sortCol!];
      const sort =
        (isSet(keyA) && isSet(keyB) && keyA > keyB) || (isSet(keyA) && !isSet(keyB)) ? 1 : -1;
      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    return sortedList;
  }

  public render() {
    const indexList = this.getConditionList();
    const items = indexList.map((curItem) => {
      return <Condition key={curItem} index={curItem} showDetail={this.props.showDetail} />;
    });

    return (
      <div id="conditions" className="flex-self flex-content mb-3">
        <div className="tableSearchHeader mx-2">
          <h1>
            <FormattedMessage id="Conditions" />
          </h1>
        </div>
        {items.length ?
          <table className="datatable fullwidthwhite">
            <thead>
              <tr>
                {this.props.showId && <th className="optional-md">Id</th>}
                {makeSortHeader(
                  TABLE_NAME,
                  "name",
                  <FormattedMessage id="Name" />,
                  this.props.tableSort ?? { column: "name", isSortAscending: true },
                  this.props.onSort,
                )}
                {makeSortHeader(
                  TABLE_NAME,
                  "value",
                  <FormattedMessage id="Value" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["value-col", "right"],
                )}
              </tr>
            </thead>
            <tbody>{items}</tbody>
          </table>
        : <FormattedMessage id="No conditions present." />}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ConditionListComponent));
